import { useMutation, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useEffect } from 'react';
import { PATIENTS_PER_PAGE } from 'components/organisms/rpm-overview-dashboard';
import { getSlaListQueryKey } from 'utils/hooks/rpmOverviewDashboard/useSlaList';
import { showResult } from 'infrastructure/hooks/utils';

import { buildRpmOverviewDashboardFilter } from '../overview-dashboard/utils';
import { getAssigneeFiltersCountQueryKey } from '../overview-dashboard/use-assignee-filters-count';
import { getAssignToolQueryKey } from './use-get-assign-tool';
import {
  getRpmOverviewDashboardCountFilter,
  getRpmOverviewDashboardCountQueryKey,
  getRpmOverviewDashboardQueryKey,
  useOverviewDashboardFilter,
} from '../overview-dashboard';

import type { IRpmPatientActivity } from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { QueryKey } from '@tanstack/react-query';
import type { RpmOverviewDashboardResponse } from '../overview-dashboard';
import type { ISetAssignPatientResponse } from 'infrastructure/interfaces';

const url = 'reports/reports/interactive-time-dashboard-assign';

const defaultData = {
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
};

export type TDurationInMinutes = 60 | 120 | 180;

interface IDataProps {
  patientGuid: string;
  action: 'assign' | 'unassign';
  durationInMinutes?: TDurationInMinutes;
  silentUpdate?: boolean;
}

const mutationFn = async (dataProps: IDataProps) => {
  const { silentUpdate = false, ...props } = dataProps;
  if (silentUpdate) {
    return;
  }

  const { data, error } =
    await dataService.updateOnly<ISetAssignPatientResponse>(url, {
      ...props,
      ...defaultData,
    });

  if (error) {
    return Promise.reject(error);
  }

  return data;
};

export const useAssignPatient = (getActivityQueryKey?: QueryKey) => {
  const queryClient = useQueryClient();
  const { filter } = useOverviewDashboardFilter();
  const overviewQueryKeyFilter = buildRpmOverviewDashboardFilter(filter, {
    withPagination: true,
    itemsPerPage: PATIENTS_PER_PAGE,
  });
  const assigneeCountQueryKeyFilter = buildRpmOverviewDashboardFilter(filter);

  const rpmOverviewDashboardCountQueryKeyFilter =
    getRpmOverviewDashboardCountFilter(filter);

  const { mutate, isLoading, error } = useMutation<
    ISetAssignPatientResponse | undefined,
    string | undefined,
    IDataProps
  >({
    mutationFn,
    async onSuccess(data, vars) {
      if (!vars?.silentUpdate) {
        const assignToolQueryKey = getAssignToolQueryKey();
        await queryClient.invalidateQueries(assignToolQueryKey);
        showResult();
      }

      const rpmOverviewDashboardQueryKey = getRpmOverviewDashboardQueryKey(
        overviewQueryKeyFilter,
      );
      const assigneeFiltersCountQueryKey = getAssigneeFiltersCountQueryKey(
        assigneeCountQueryKeyFilter,
      );
      const rpmOverviewDashboardCountQueryKey =
        getRpmOverviewDashboardCountQueryKey(
          rpmOverviewDashboardCountQueryKeyFilter,
        );

      queryClient.invalidateQueries(rpmOverviewDashboardQueryKey);
      queryClient.invalidateQueries(getSlaListQueryKey(filter));
      queryClient.invalidateQueries(assigneeFiltersCountQueryKey);
      queryClient.invalidateQueries(rpmOverviewDashboardCountQueryKey);

      queryClient.setQueryData<RpmOverviewDashboardResponse>(
        rpmOverviewDashboardQueryKey,
        (queryData) => {
          if (!queryData) return queryData;

          const newData = structuredClone(queryData);
          newData.data.forEach((item) => {
            if (vars?.silentUpdate && item.guid === vars?.patientGuid) {
              item.assignee = undefined;
              return;
            }

            if (item.guid === data?.data.patientGuid) {
              item.assignee = data?.data.assignee;
            }
          });

          return newData;
        },
      );

      // for old rpm dashboard. Remove after overview dashboard will be used as primary
      if (getActivityQueryKey) {
        queryClient.setQueryData<IRpmPatientActivity>(
          getActivityQueryKey,
          (oldData) => {
            if (oldData) {
              const newData = structuredClone(oldData);

              newData.data.forEach((item) => {
                if (item.patient.guid === data?.data.patientGuid) {
                  if (!data?.data?.assignee && item.reserved) {
                    item.reserved = null;
                  }
                  if (data?.data?.assignee && !item.reserved) {
                    const splitByDot = data.data.assignee.fullName.split('.');
                    const title = splitByDot?.[0];
                    const splitByDash = splitByDot?.[1]?.split('-');
                    const splitBySpace = splitByDash?.[0]?.split(' ');
                    const firstName = splitBySpace?.[0];
                    const lastName = splitBySpace?.[1];
                    const professionType = splitByDash?.[1];

                    const reserved = {
                      loginGuid: data.data.assignee.loginGuid,
                      firstName: firstName?.trim(),
                      lastName: lastName?.trim(),
                      title: `${title}.`,
                      professionType: professionType?.trim(),
                    };
                    item.reserved = reserved;
                  }
                }
              });

              return newData;
            }

            return oldData;
          },
        );
      }
    },
  });

  useEffect(() => {
    if (error) {
      showResult(error);
    }
  }, [error]);

  return { assignPatient: mutate, isLoading };
};
